import { publishEvent } from './sync';
import { getCity } from './location';
import type { EventListingPayload, EventRSVPPayload } from './events';

const LISTINGS_KEY = 'authenticulture_event_listings';
const RSVPS_KEY = 'authenticulture_rsvps';
const MAX_LISTINGS = 500;
const MAX_RSVPS = 2000;

export interface EventListing {
  id: string;
  pubkey: string;
  sig: string;
  timestamp: number;
  title: string;
  description: string;
  startTime: number;
  endTime?: number;
  city: string;
  country?: string;
  venue?: string;
  tags?: string[];
  links?: Array<{ label: string; url: string }>;
  communityId?: string;
  flyerMagnetURI?: string;
  flyerType?: 'image' | 'video';
}

export interface RSVP {
  id: string;
  pubkey: string;
  sig: string;
  timestamp: number;
  eventId: string;
  status: 'going' | 'interested';
}

export type EventFeedMode = 'local' | 'trending' | 'following' | 'fresh' | 'soon';

function loadListings(): EventListing[] {
  try {
    return JSON.parse(localStorage.getItem(LISTINGS_KEY) ?? '[]');
  } catch {
    return [];
  }
}

function loadRSVPs(): RSVP[] {
  try {
    return JSON.parse(localStorage.getItem(RSVPS_KEY) ?? '[]');
  } catch {
    return [];
  }
}

function isExpired(listing: EventListing): boolean {
  const cutoff = listing.endTime ?? (listing.startTime + 24 * 3_600_000);
  return Date.now() > cutoff;
}

export function saveEventListing(event: {
  id: string;
  pubkey: string;
  sig: string;
  timestamp: number;
  payload: EventListingPayload;
}): void {
  const p = event.payload;
  const listing: EventListing = {
    id: event.id,
    pubkey: event.pubkey,
    sig: event.sig,
    timestamp: event.timestamp,
    title: p.title,
    description: p.description,
    startTime: p.startTime,
    endTime: p.endTime,
    city: p.city,
    country: p.country,
    venue: p.venue,
    tags: p.tags?.map(t => t.toLowerCase().trim()).filter(Boolean),
    links: p.links,
    communityId: p.communityId,
    flyerMagnetURI: p.flyerMagnetURI,
    flyerType: p.flyerType,
  };
  const listings = loadListings();
  if (listings.some(l => l.id === listing.id)) return;
  listings.unshift(listing);
  if (listings.length > MAX_LISTINGS) listings.splice(MAX_LISTINGS);
  localStorage.setItem(LISTINGS_KEY, JSON.stringify(listings));
}

export function saveRSVP(event: {
  id: string;
  pubkey: string;
  sig: string;
  timestamp: number;
  payload: EventRSVPPayload;
}): void {
  const rsvp: RSVP = {
    id: event.id,
    pubkey: event.pubkey,
    sig: event.sig,
    timestamp: event.timestamp,
    eventId: event.payload.eventId,
    status: event.payload.status,
  };
  const rsvps = loadRSVPs();
  if (rsvps.some(r => r.id === rsvp.id)) return;
  rsvps.push(rsvp);
  if (rsvps.length > MAX_RSVPS) rsvps.splice(0, rsvps.length - MAX_RSVPS);
  localStorage.setItem(RSVPS_KEY, JSON.stringify(rsvps));
}

export function deleteEventListing(listingId: string, pubkey: string): boolean {
  const listings = loadListings();
  const index = listings.findIndex(l => l.id === listingId && l.pubkey === pubkey);
  if (index === -1) return false;
  listings.splice(index, 1);
  localStorage.setItem(LISTINGS_KEY, JSON.stringify(listings));
  return true;
}

export function getEventsByCity(city: string, limit = 50): EventListing[] {
  return loadListings()
    .filter(l => l.city.toLowerCase() === city.toLowerCase())
    .slice(0, limit);
}

export function getUpcomingEvents(limit = 200): EventListing[] {
  return loadListings().filter(l => !isExpired(l)).slice(0, limit);
}

export function getRSVPCount(eventId: string): { going: number; interested: number } {
  const rsvps = loadRSVPs().filter(r => r.eventId === eventId);
  const latestByPubkey = new Map<string, RSVP>();
  for (const r of rsvps) {
    const existing = latestByPubkey.get(r.pubkey);
    if (!existing || r.timestamp > existing.timestamp) latestByPubkey.set(r.pubkey, r);
  }
  let going = 0;
  let interested = 0;
  for (const r of Array.from(latestByPubkey.values())) {
    if (r.status === 'going') going++;
    else if (r.status === 'interested') interested++;
  }
  return { going, interested };
}

export function getUserRSVP(eventId: string, pubkey: string): 'going' | 'interested' | null {
  const userRSVPs = loadRSVPs()
    .filter(r => r.eventId === eventId && r.pubkey === pubkey)
    .sort((a, b) => b.timestamp - a.timestamp);
  return userRSVPs[0]?.status ?? null;
}

function getFollowedPubkeys(userId: string): Set<string> {
  try {
    const follows = JSON.parse(localStorage.getItem('authenticulture_follows') ?? '[]') as Array<{
      followerUserId: string;
      followeeUserId: string;
    }>;
    return new Set(follows.filter(f => f.followerUserId === userId).map(f => f.followeeUserId));
  } catch {
    return new Set();
  }
}

export function getEventFeedListings(
  mode: EventFeedMode,
  opts: { city?: string; userId?: string; limit?: number } = {}
): EventListing[] {
  const { city, userId, limit = 50 } = opts;
  const upcoming = getUpcomingEvents();

  switch (mode) {
    case 'local': {
      const filterCity = city ?? getCity()?.city;
      if (!filterCity) return [...upcoming].sort((a, b) => a.startTime - b.startTime).slice(0, limit);
      return upcoming
        .filter(l => l.city.toLowerCase() === filterCity.toLowerCase())
        .sort((a, b) => a.startTime - b.startTime)
        .slice(0, limit);
    }

    case 'soon':
      return [...upcoming].sort((a, b) => a.startTime - b.startTime).slice(0, limit);

    case 'fresh':
      return [...upcoming].sort((a, b) => b.timestamp - a.timestamp).slice(0, limit);

    case 'following': {
      if (!userId) return upcoming.slice(0, limit);
      const followed = getFollowedPubkeys(userId);
      return upcoming
        .filter(l => followed.has(l.pubkey))
        .sort((a, b) => a.startTime - b.startTime)
        .slice(0, limit);
    }

    case 'trending': {
      const cutoff = Date.now() - 48 * 3_600_000;
      const recentRSVPs = loadRSVPs().filter(r => r.timestamp >= cutoff);
      const velocity = new Map<string, number>();
      for (const r of recentRSVPs) {
        velocity.set(r.eventId, (velocity.get(r.eventId) ?? 0) + (r.status === 'going' ? 2 : 1));
      }
      return [...upcoming]
        .sort((a, b) => (velocity.get(b.id) ?? 0) - (velocity.get(a.id) ?? 0))
        .slice(0, limit);
    }

    default:
      return upcoming.slice(0, limit);
  }
}

export async function publishEventListing(
  title: string,
  description: string,
  startTime: number,
  opts: {
    endTime?: number;
    venue?: string;
    tags?: string[];
    links?: Array<{ label: string; url: string }>;
    communityId?: string;
    flyerMagnetURI?: string;
    flyerType?: 'image' | 'video';
  } = {}
): Promise<void> {
  const location = getCity();
  const id = `evt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const payload: EventListingPayload = {
    id,
    title,
    description,
    startTime,
    city: location?.city ?? '',
    country: location?.country,
    ...opts,
  };
  await publishEvent('event_listing', payload as any);
}

export async function publishRSVP(
  eventId: string,
  status: 'going' | 'interested'
): Promise<void> {
  const payload: EventRSVPPayload = { eventId, status };
  await publishEvent('event_rsvp', payload as any);
}
